import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Notify, Loading} from 'notiflix';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline,
  ArrowBack,
  CheckCircle,
  Delete as DeleteIcon,
  DragIndicator,
  Save,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {understandItBetterApi} from 'api/understandItBetterApi';

// ─── Constants ────────────────────────────────────────────────────────────────
const ENTITY_TYPE_META = {
  products: {
    label: 'Product',
    color: '#1565c0',
    bg: '#e3f2fd',
    lightBg: '#f0f7ff',
  },
  valuepacks: {
    label: 'Value Pack',
    color: '#7b1fa2',
    bg: '#f3e5f5',
    lightBg: '#fdf4ff',
  },
  plans: {
    label: 'Question & Answer',
    color: '#2e7d32',
    bg: '#e8f5e9',
    lightBg: '#f0fdf4',
  },
};

// ─── emptySection helper ──────────────────────────────────────────────────────
const emptySection = (
  isNew = true,
  dbId = null,
  title = '',
  description = '',
) => ({
  _localId: Date.now() + Math.random(),
  isNew,
  dbId,
  title,
  description,
});

// ─── Component ────────────────────────────────────────────────────────────────
const UnderstandItBetterEdit = () => {
  const navigate = useNavigate();
  const {entityType, entityId} = useParams();

  const [sections, setSections] = useState([]);
  const [entityName, setEntityName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const entityMeta = ENTITY_TYPE_META[entityType] || {
    label: entityType,
    color: '#475569',
    bg: '#f1f5f9',
    lightBg: '#f8fafc',
  };

  const newSectionCount = sections.filter(s => s.isNew).length;
  const existingSectionCount = sections.filter(s => !s.isNew).length;

  // ─── Load sections ───────────────────────────────────────────────────────
  useEffect(() => {
    if (entityType && entityId) fetchSections();
  }, [entityType, entityId]);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const data = await understandItBetterApi.getByEntity(
        entityType,
        entityId,
      );
      const list = Array.isArray(data) ? data : [];

      setSections(
        list.map(s =>
          emptySection(false, s.id, s.title || '', s.description || ''),
        ),
      );

      if (list.length > 0) {
        setEntityName(list[0].entityName || '');
      }
    } catch (err) {
      Notify.failure('Failed to load sections');
      navigate('/admin/understand-it-better');
    } finally {
      setLoading(false);
    }
  };

  // ─── Section handlers ────────────────────────────────────────────────────
  const addSection = () => setSections(prev => [...prev, emptySection(true)]);

  const removeSection = async localId => {
    if (sections.length === 1) {
      Notify.warning('At least one section is required');
      return;
    }

    const section = sections.find(s => s._localId === localId);

    // Agar DB mein hai to pehle delete karo
    if (section?.dbId) {
      Loading.circle('Removing section...');
      try {
        await understandItBetterApi.deleteOne(section.dbId);
        Notify.success('Section removed');
      } catch (err) {
        Notify.failure(err.message || 'Failed to remove section');
        return;
      } finally {
        Loading.remove();
      }
    }

    setSections(prev => prev.filter(s => s._localId !== localId));
  };

  const updateSection = (localId, field, value) => {
    setSections(prev =>
      prev.map(s => (s._localId === localId ? {...s, [field]: value} : s)),
    );
    setErrors(prev => {
      const updated = {...prev};
      delete updated[`${localId}_${field}`];
      return updated;
    });
  };

  // ─── Validation ──────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};
    sections.forEach(s => {
      if (!s.title.trim())
        newErrors[`${s._localId}_title`] = 'Title is required';
      if (!s.description.trim())
        newErrors[`${s._localId}_description`] = 'Description is required';
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) {
      Notify.failure('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      Loading.circle('Saving...');

      await Promise.all(
        sections.map(s =>
          s.isNew
            ? understandItBetterApi.createOne({
                entityId,
                entityType,
                title: s.title,
                description: s.description,
              })
            : understandItBetterApi.updateOne(s.dbId, {
                title: s.title,
                description: s.description,
              }),
        ),
      );

      setSuccess(true);
      setTimeout(() => navigate('/admin/understand-it-better'), 1500);
    } catch (err) {
      Notify.failure(err.message || 'Failed to save sections');
    } finally {
      Loading.remove();
      setSubmitting(false);
    }
  };

  // ─── Success screen ───────────────────────────────────────────────────────
  if (success) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh">
          <Card
            sx={{
              p: 6,
              textAlign: 'center',
              maxWidth: 420,
              border: '2px solid #86efac',
              bgcolor: '#f0fdf4',
            }}>
            <CheckCircle sx={{fontSize: 80, color: '#15803d', mb: 2}} />
            <Typography
              variant="h4"
              sx={{color: '#15803d', fontWeight: 700, mb: 1}}>
              Changes Saved!
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {existingSectionCount} section(s) updated, {newSectionCount} new
              section(s) added.
            </Typography>
            <Typography variant="body2" color="textSecondary" mt={1}>
              Redirecting to list...
            </Typography>
          </Card>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  // ─── Loading screen ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
          flexDirection="column">
          <CircularProgress />
          <Typography variant="h6" mt={2}>
            Loading sections...
          </Typography>
        </Box>
        <Footer />
      </DashboardLayout>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Box mx="auto" px={2}>
          {/* Header */}
          <Box display="flex" alignItems="center" mb={4}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/admin/understand-it-better')}
              sx={{mr: 2}}>
              Back
            </Button>
            <Typography variant="h4" fontWeight={700}>
              Edit Sections
            </Typography>
          </Box>

          {/* Entity info banner */}
          <Card
            sx={{
              mb: 3,
              p: 2.5,
              bgcolor: entityMeta.bg,
              border: `1px solid ${entityMeta.color}40`,
            }}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Chip
                label={entityMeta.label}
                size="small"
                sx={{
                  bgcolor: entityMeta.bg,
                  color: entityMeta.color,
                  fontWeight: 700,
                }}
              />
              <Typography variant="body1" fontWeight={700} color="#1e3a5f">
                {entityName || entityId}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {existingSectionCount} existing · {newSectionCount} new
              </Typography>
            </Box>
          </Card>

          {/* Info alert */}
          <Alert severity="info" sx={{mb: 3}}>
            <strong>Edit Mode:</strong> Existing sections update honge, naye
            sections create honge. Section delete karne ke liye trash icon use
            karo.
          </Alert>

          {/* Section cards */}
          <Box display="flex" flexDirection="column" gap={2} mb={3}>
            {sections.map((section, index) => (
              <Card
                key={section._localId}
                sx={{
                  border: section.isNew
                    ? '1px solid #86efac'
                    : '1px solid #e2e8f0',
                  transition: 'box-shadow 0.2s',
                  '&:hover': {boxShadow: '0 4px 16px rgba(0,0,0,0.08)'},
                }}>
                <CardContent sx={{p: 3}}>
                  {/* Card header */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2.5}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DragIndicator sx={{color: '#94a3b8', fontSize: 20}} />
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="#1e3a5f">
                        Section {index + 1}
                      </Typography>
                      <Chip
                        label={section.isNew ? 'New' : 'Existing'}
                        size="small"
                        sx={{
                          bgcolor: section.isNew ? '#dcfce7' : '#e0f2fe',
                          color: section.isNew ? '#15803d' : '#0369a1',
                          fontWeight: 600,
                          fontSize: '0.65rem',
                        }}
                      />
                      {section.title && (
                        <Chip
                          label={
                            section.title.length > 30
                              ? `${section.title.slice(0, 30)}...`
                              : section.title
                          }
                          size="small"
                          sx={{
                            bgcolor: '#f1f5f9',
                            color: '#475569',
                            fontSize: '0.7rem',
                          }}
                        />
                      )}
                    </Box>

                    <Tooltip title="Remove section">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => removeSection(section._localId)}
                          disabled={sections.length === 1}
                          sx={{
                            color: '#ef4444',
                            '&:hover': {bgcolor: '#fef2f2'},
                            '&:disabled': {color: '#cbd5e1'},
                          }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  {/* Fields */}
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Title *"
                        value={section.title}
                        onChange={e =>
                          updateSection(
                            section._localId,
                            'title',
                            e.target.value,
                          )
                        }
                        error={!!errors[`${section._localId}_title`]}
                        helperText={
                          errors[`${section._localId}_title`] ||
                          `${section.title.length}/120 characters`
                        }
                        inputProps={{maxLength: 120}}
                        placeholder="e.g. What makes this product unique?"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description *"
                        value={section.description}
                        onChange={e =>
                          updateSection(
                            section._localId,
                            'description',
                            e.target.value,
                          )
                        }
                        error={!!errors[`${section._localId}_description`]}
                        helperText={
                          errors[`${section._localId}_description`] ||
                          `${section.description.length} characters`
                        }
                        placeholder="Enter a detailed description..."
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Add section button */}
          <Button
            fullWidth
            variant="outlined"
            onClick={addSection}
            startIcon={<AddCircleOutline />}
            sx={{
              mb: 3,
              py: 1.5,
              borderStyle: 'dashed',
              borderColor: '#1976d2',
              color: '#1976d2',
              bgcolor: '#f0f9ff',
              '&:hover': {bgcolor: '#dbeafe', borderStyle: 'dashed'},
            }}>
            Add Another Section
          </Button>

          {/* Action bar */}
          <Card sx={{p: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0'}}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={2}>
              <Typography variant="body2" color="textSecondary">
                {existingSectionCount} section(s) will be updated ·{' '}
                {newSectionCount} new section(s) will be created
              </Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/understand-it-better')}
                  disabled={submitting}
                  sx={{
                    color: 'grey.600',
                    borderColor: 'grey.400',
                    '&:hover': {
                      borderColor: 'grey.600',
                      backgroundColor: 'grey.100',
                    },
                  }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  startIcon={
                    submitting ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Save />
                    )
                  }
                  sx={{
                    bgcolor: '#d97706',
                    color: '#fff',
                    px: 4,
                    '&:hover': {bgcolor: '#b45309'},
                  }}>
                  {submitting
                    ? 'Saving...'
                    : `Save Changes (${sections.length})`}
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default UnderstandItBetterEdit;
